import React, { useState } from "react";
import styled from "styled-components";
import styles from "styles/styles";
import Dropdown from "components/common/ui/Dropdown/Dropdown";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";

const PaginationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
    padding: 1rem 0;
`;

const PaginationInfo = styled.div`
    font-size: 14px;
    color: ${styles.colors.grey6};
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const PaginationButton = styled.button`
    padding: 0.5rem 0.75rem;
    border: none;
    background-color: transparent;
    color: ${styles.colors.red};
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        cursor: not-allowed;
        color: ${styles.colors.grey4};
    }
`;

const DropdownItem = styled.div`
    padding: 8px 12px;
    cursor: pointer;
    color: ${styles.colors.grey6};
    &:hover {
        background-color: #f0f0f0;
    }
`;

const DropdownButton = styled.button<{ isOpen: boolean }>`
    background: white;
    border: 1px solid ${styles.colors.black};
    font-size: 14px;
    font-weight: 400;
    padding: 0.5rem 28px 0.5rem 0.5rem;
    text-align: left;
    color: ${styles.colors.grey6};
    margin-left: 0.5rem;

    &::after {
        content: "";
        position: absolute;
        top: 50%;
        right: 8px;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 4px solid currentColor;
        transform: translateY(-50%) ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0deg)")};
        transition: transform 0.2s ease-in-out;
    }
`;

const ItemsPerPageContainer = styled.div`
    display: flex;
    align-items: center;
    font-size: 14px;
    color: ${styles.colors.grey6};
`;

export interface PaginationProps {
    totalItems: number;
    itemsPerPage: number;
    startIndex: number;
    endIndex: number;
    canGoToNextPage: boolean;
    canGoToPreviousPage: boolean;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    goToFirstPage: () => void;
    goToLastPage: () => void;
    onItemsPerPageChange: (itemsPerPage: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
    totalItems,
    itemsPerPage,
    startIndex,
    endIndex,
    canGoToNextPage,
    canGoToPreviousPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    onItemsPerPageChange,
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const itemsPerPageOptions = [5, 10, 25, 50];
    const translate = useTranslate();

    if (totalItems === 0) {
        return null;
    }

    const handleItemsPerPageChange = (value: string) => {
        onItemsPerPageChange(Number(value));
        setIsDropdownOpen(false);
    };

    return (
        <>
            <PaginationContainer>
                <ItemsPerPageContainer>
                    {translate("featureRequests.pagination.rowsPerPage")}
                    <Dropdown
                        isOpen={isDropdownOpen}
                        toggle={setIsDropdownOpen}
                        position="left"
                        contentClassName="pagination-dropdown-content"
                        renderButtonContent={() => (
                            <DropdownButton isOpen={isDropdownOpen}>{itemsPerPage}</DropdownButton>
                        )}
                        renderContent={() => (
                            <div>
                                {itemsPerPageOptions.map((option) => (
                                    <DropdownItem
                                        key={option}
                                        onClick={() => handleItemsPerPageChange(String(option))}
                                    >
                                        {option}
                                    </DropdownItem>
                                ))}
                            </div>
                        )}
                    />
                </ItemsPerPageContainer>

                <PaginationControls>
                    <PaginationButton onClick={goToFirstPage} disabled={!canGoToPreviousPage}>
                        «
                    </PaginationButton>
                    <PaginationButton onClick={goToPreviousPage} disabled={!canGoToPreviousPage}>
                        ‹
                    </PaginationButton>

                    <PaginationInfo>
                        {startIndex}-{endIndex} / {totalItems}
                    </PaginationInfo>

                    <PaginationButton onClick={goToNextPage} disabled={!canGoToNextPage}>
                        ›
                    </PaginationButton>
                    <PaginationButton onClick={goToLastPage} disabled={!canGoToNextPage}>
                        »
                    </PaginationButton>
                </PaginationControls>
            </PaginationContainer>
        </>
    );
};

export default Pagination;
